import React, { JSX } from "react";
import Loader from "./Loader";

interface VerticalTableProps {
  headers: string[];
  data: { category: string; values: string[] }[];
  loading: boolean;
  isComparative?: boolean;
  branchCount?: number;
  branchColors?: Record<string, { bgColor: string; textColor: string; borderColor: string }>;
  branchIds?: string[];
}

const VerticalTable3: React.FC<VerticalTableProps> = ({
  headers,
  data,
  loading,
  isComparative = false,
  branchCount = 0,
  branchColors = {},
  branchIds = [],
}) => {
  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Loader />
      </div>
    );

  if (!data?.length)
    return <p>No data available for the selected criteria</p>;

  // Helper: Split header into period and branch
  const splitHeader = (h: string) => {
    const m = h.match(/^(.*?)\s*\((.*?)\)\s*$/);
    return m ? { period: m[1].trim(), branch: m[2].trim() } : { period: h, branch: "" };
  };

  // Helper: Style each cell based on comparative mode and branch
  const getCellStyle = (colIdx: number, isCategory = false, isHeader = false) => {
    if (!isComparative || isCategory) return {};

    const branchIdx = (colIdx - 1) % branchCount;
    const branchId = branchIds[branchIdx];
    const color = branchColors[branchId];

    if (!color || branchIdx < 0) return {};

    return isHeader
      ? {
          backgroundColor: color.bgColor,
          color: color.textColor,
          position: "relative" as const,
          borderBottom: `2px solid ${color.borderColor}`,
        }
      : {
          backgroundColor: `${color.bgColor}`,
          borderLeft: `3px solid ${color.borderColor}`,
          position: "relative" as const,
        };
  };

  // Category column cell (left-most)
  const categoryHead = (
    <th
      className="tableHead"
      style={{
        textAlign: "center",
        fontWeight: 500,
        padding: "8px",
        backgroundColor: "#f8f9fa",
      }}
    >
      
    </th>
  );

  let headerRow1: JSX.Element;
  let headerRow2: JSX.Element;

  if (isComparative && branchCount > 0) {
    // Period-level headers
    const periodCells: JSX.Element[] = [];
    for (let i = 1; i < headers.length; i += branchCount) {
      const { period } = splitHeader(headers[i]);
      const colSpan = Math.min(branchCount, headers.length - i);
      periodCells.push(
        <th
          key={i}
          colSpan={colSpan}
          className="tableHead"
          style={{
            textAlign: "center",
            fontWeight: 500,
            padding: "8px",
            backgroundColor: "white",
          }}
        >
          {period}
        </th>
      );
    }

    // Branch-level headers
    const branchCells = headers.slice(1).map((h, idx) => {
      const { branch } = splitHeader(h);
      const style = getCellStyle(idx + 1, false, true);
      const branchIdx = (idx) % branchCount;
      const branchId = branchIds[branchIdx];
      const color = branchColors[branchId];

      return (
        <th
          key={idx}
          className="tableHead"
          style={{
            textAlign: "center",
            padding: "8px",
            fontWeight: 400,
            ...style,
          }}
        >
          {branch}
          {color && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "3px",
                backgroundColor: color.borderColor,
              }}
            />
          )}
        </th>
      );
    });

    headerRow1 = (
      <tr style={{ fontSize: 12, fontWeight: 300 }}>
        {[categoryHead, ...periodCells]}
      </tr>
    );
    headerRow2 = (
      <tr style={{ fontSize: 12, fontWeight: 300 }}>
        {[categoryHead, ...branchCells]}
      </tr>
    );
  } else {
    // Non-comparative mode: single flat header row
    const cells = headers.map((h, idx) => (
      <th
        key={idx}
        className="tableHead"
        style={{
          textAlign: "center",
          fontWeight: 500,
          padding: "8px",
          ...getCellStyle(idx),
          backgroundColor: idx === 0 ? "#f8f9fa" : "white",
        }}
      >
        {h}
      </th>
    ));
    headerRow1 = <tr style={{ fontSize: 12, fontWeight: 300 }}>{cells}</tr>;
    headerRow2 = <></>;
  }

  // Main table rendering
  return (
    <div className="table-responsive" style={{ overflowX: "auto" }}>
      <table
        className="table table-bordered table-hover"
        style={{ margin: "20px", whiteSpace: "nowrap" }}
      >
        <thead>
          {headerRow1}
          {headerRow2}
        </thead>

        <tbody style={{ fontSize: 13, fontWeight: 400 }}>
          {data.map((row, rIdx) => (
            <tr key={rIdx}>
              <td
                className="text-start align-middle ps-3"
                style={{
                  fontWeight: 500,
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {row.category}
              </td>
              {row.values.map((val, cIdx) => (
                <td
                  key={cIdx}
                  style={{
                    textAlign: "center",
                    padding: "8px",
                    ...getCellStyle(cIdx + 1),
                  }}
                >
                  {val}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VerticalTable3;